import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, CheckCircle2, XCircle, Lock } from "lucide-react";
import api, { fmtErr } from "@/lib/api";
import { useLang } from "@/i18n/LangContext";

export default function ResetPassword() {
  const { lang, setLang, t } = useLang();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const isInvite = params.get("invite") === "1";

  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [email, setEmail] = useState("");

  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      if (!token) {
        setValid(false);
        setChecking(false);
        return;
      }
      try {
        const { data } = await api.get(`/auth/verify-reset-token/${encodeURIComponent(token)}`);
        if (!cancel) {
          setValid(true);
          setEmail(data.email || "");
        }
      } catch {
        if (!cancel) setValid(false);
      } finally {
        if (!cancel) setChecking(false);
      }
    })();
    return () => { cancel = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pwd.length < 6) return setErr(t("reset.min_length"));
    if (pwd !== pwd2) return setErr(t("reset.mismatch"));
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setDone(true);
      setTimeout(() => nav("/login"), 3200);
    } catch (ex) {
      setErr(fmtErr(ex));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F8FA] flex items-center justify-center p-6" data-testid="reset-password-page">
      <div className="absolute top-4 right-4">
        <div className="inline-flex bg-white/90 backdrop-blur rounded-full p-1 border border-slate-200 shadow-sm">
          <button type="button" onClick={() => setLang("es")}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${lang === "es" ? "bg-[var(--ink)] text-white" : "text-slate-600"}`}
                  data-testid="reset-lang-es">🇪🇸 ES</button>
          <button type="button" onClick={() => setLang("en")}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${lang === "en" ? "bg-[var(--ink)] text-white" : "text-slate-600"}`}
                  data-testid="reset-lang-en">🇬🇧 EN</button>
        </div>
      </div>

      <div className="w-full max-w-md sns-fade-up">
        <div className="flex justify-center mb-6">
          <img src="/images/logo-brand.png" alt="Smile & Shine" className="h-20 rounded-3xl shadow-lg shadow-slate-300/50" />
        </div>

        {checking && (
          <div className="text-center py-12" data-testid="reset-checking">
            <div className="w-10 h-10 border-4 border-slate-200 border-t-[var(--coral)] rounded-full animate-spin mx-auto mb-4" />
            <p className="text-slate-500">{t("reset.checking")}</p>
          </div>
        )}

        {!checking && !valid && (
          <div className="text-center" data-testid="reset-invalid">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 text-red-600 mb-4">
              <XCircle className="w-8 h-8" />
            </div>
            <h2 className="sns-title text-2xl mb-3">{t("reset.invalid_title")}</h2>
            <p className="text-slate-600 mb-8">{t("reset.invalid_desc")}</p>
            <Link to="/forgot-password" className="sns-btn sns-btn-accent w-full py-4 inline-block" data-testid="reset-request-new">
              {t("reset.request_new")}
            </Link>
            <div className="mt-4">
              <Link to="/login" className="text-sm text-slate-500 hover:text-[var(--coral)]">{t("forgot.back_to_login")}</Link>
            </div>
          </div>
        )}

        {!checking && valid && !done && (
          <>
            <h2 className="sns-title text-3xl mb-2">
              {isInvite
                ? (lang === "en" ? "Set your password" : "Elige tu contraseña")
                : t("reset.title")}
            </h2>
            <p className="text-slate-500 mb-6">
              {isInvite
                ? (lang === "en"
                    ? <>Welcome! Choose a password for <span className="font-semibold text-slate-700">{email}</span> and you&apos;re in.</>
                    : <>¡Bienvenido! Elige una contraseña para <span className="font-semibold text-slate-700">{email}</span> y ya puedes entrar.</>)
                : <>{t("reset.subtitle")} <span className="font-semibold text-slate-700">{email}</span></>}
            </p>

            <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
              <div>
                <label className="block text-sm font-semibold mb-2">{t("reset.new_password")}</label>
                <div className="relative">
                  <Lock className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type={show ? "text" : "password"}
                    className="sns-input pl-12 pr-12"
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    required
                    minLength={6}
                    autoFocus
                    data-testid="reset-password-input"
                  />
                  <button type="button" onClick={() => setShow((v) => !v)}
                          className="absolute inset-y-0 right-0 flex items-center px-4 text-slate-400 hover:text-slate-600"
                          aria-label={show ? t("login.hide_password") : t("login.show_password")}
                          data-testid="reset-show-toggle">
                    {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">{t("reset.confirm_password")}</label>
                <input
                  type={show ? "text" : "password"}
                  className="sns-input"
                  value={pwd2}
                  onChange={(e) => setPwd2(e.target.value)}
                  required
                  minLength={6}
                  data-testid="reset-confirm-input"
                />
              </div>

              {err && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3" data-testid="reset-error">
                  {err}
                </div>
              )}

              <button type="submit"
                      disabled={loading || !pwd || !pwd2}
                      className="sns-btn sns-btn-accent w-full text-base py-4 disabled:opacity-60"
                      data-testid="reset-submit-button">
                {loading ? t("reset.saving") : t("reset.submit")}
              </button>
            </form>
          </>
        )}

        {done && (
          <div className="text-center" data-testid="reset-done">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 mb-4">
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <h2 className="sns-title text-2xl mb-3">{t("reset.done_title")}</h2>
            <p className="text-slate-600 mb-2">{t("reset.done_desc")}</p>
            <p className="text-xs text-slate-400">{t("reset.redirecting")}</p>
          </div>
        )}
      </div>
    </div>
  );
}
